import { MinorNightWorkRule } from 'constants/MinorNightWorkRule';
import { TFunction } from 'i18next';
import {
  CONTRACT_COMPLIANCE__AGE_BAND_RANGE,
  CONTRACT_COMPLIANCE__DEFAULT_NIGHT_WORK_AGE_BAND,
  CONTRACT_COMPLIANCE__REQUIRED_WHOLE_YEARS,
  CONTRACT_COMPLIANCE__RULE_NOT_APPLIED,
} from 'translations/constants';
import {
  IJustificationPayload,
  IMinorNightWorkRuleDefault,
  IRuleException,
  ISaveMinorNightWorkDefault,
} from 'types/contract-compliance/IContractCompliance';
import { formatInterval, validateInterval } from '../interval';
import { parseMinutesInput } from '../utils';
import { hasAgeBand, MAX_MINOR_AGE_YEARS } from './constants';

export type NightWorkDefaultDraft = {
  start: string;
  end: string;
  minAge: string;
  maxAge: string;
};

export type NightWorkDefaultDraftErrors = Partial<Record<keyof NightWorkDefaultDraft, string>>;

const toYearsText = (value: number | null): string => (value === null ? '' : String(value));

export const toNightWorkDefaultDraft = (value: IMinorNightWorkRuleDefault): NightWorkDefaultDraft => ({
  start: value.startTime,
  end: value.endTime,
  minAge: toYearsText(value.minAgeYears),
  maxAge: toYearsText(value.maxAgeYears),
});

export const validateNightWorkDefaultDraft = (
  rule: MinorNightWorkRule,
  draft: NightWorkDefaultDraft,
): NightWorkDefaultDraftErrors => {
  const errors: NightWorkDefaultDraftErrors = { ...validateInterval({ start: draft.start, end: draft.end }, true) };
  if (!hasAgeBand(rule)) return errors;
  const minAge = parseMinutesInput(draft.minAge);
  const maxAge = parseMinutesInput(draft.maxAge);
  if (minAge === null) errors.minAge = CONTRACT_COMPLIANCE__REQUIRED_WHOLE_YEARS;
  if (maxAge === null) errors.maxAge = CONTRACT_COMPLIANCE__REQUIRED_WHOLE_YEARS;
  else if (maxAge > MAX_MINOR_AGE_YEARS || (minAge !== null && minAge > maxAge)) {
    errors.maxAge = CONTRACT_COMPLIANCE__AGE_BAND_RANGE;
  }
  return errors;
};

export const isNightWorkDefaultComplete = (rule: MinorNightWorkRule, draft: NightWorkDefaultDraft): boolean =>
  draft.start.trim() !== '' &&
  draft.end.trim() !== '' &&
  (!hasAgeBand(rule) || (draft.minAge.trim() !== '' && draft.maxAge.trim() !== ''));

export const toSaveNightWorkDefaultBody = (
  rule: MinorNightWorkRule,
  draft: NightWorkDefaultDraft,
  rowVersion: string,
  justification: IJustificationPayload,
): ISaveMinorNightWorkDefault => ({
  startTime: draft.start.trim(),
  endTime: draft.end.trim(),
  minAgeYears: hasAgeBand(rule) ? Number(draft.minAge) : null,
  maxAgeYears: hasAgeBand(rule) ? Number(draft.maxAge) : null,
  rowVersion,
  ...justification,
});

export const formatNightWorkDefault = (t: TFunction, value: IMinorNightWorkRuleDefault): string => {
  const interval = formatInterval(value.startTime, value.endTime);
  if (value.minAgeYears === null || value.maxAgeYears === null) return interval;
  return t(CONTRACT_COMPLIANCE__DEFAULT_NIGHT_WORK_AGE_BAND, {
    interval,
    minAge: value.minAgeYears,
    maxAge: value.maxAgeYears,
  });
};

export const formatNightWorkExceptionValue = (t: TFunction, row: IRuleException): string =>
  row.startTime && row.endTime ? formatInterval(row.startTime, row.endTime) : t(CONTRACT_COMPLIANCE__RULE_NOT_APPLIED);
